import {
	ActionButton,
	ButtonContainer,
	Form,
	FormContainer,
	QueryError,
} from "@components";
import { useAsyncCommand, useServices, useStaticData } from "@hooks";
import { Division } from "@models";
import {
	checkPricingSettings,
	initialPricingSettings,
} from "../../products/components/util/pricingSettingsUtils";
import React, { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import styled from "styled-components";
import { useDivisionFromPageContext } from "../hooks/useDivisionFromPageContext";
import BasicDataSettings from "./settings/BasicDataSettings";
import ExcessBudgetSettings from "./settings/ExcessBudgetSettings";
import TimerSettings from "./settings/TimerSettings";
import ModuleCategorySettings from "./settings/ModuleCategorySettings";
import PricingSettingsList from "./SettingsPage/PricingSettingsList";
import PricingSettingsPageContext from "./SettingsPage/PricingSettingsPageContext";
import ReviewSettings from "./settings/ReviewSettings";
import OtherSettings from "./settings/OtherSettings";

export default function DivisionSettingsPage() {
	const context = useDivisionFromPageContext();
	const { division } = context;
	const { products } = useStaticData();

	const defaultValues = {
		...division,
		pricingSettings: division.pricingSettings
			? initialPricingSettings(division.pricingSettings, products)
			: [],
		reviewSettings: division.reviewSettings || {
			shouldReviewTrips: false,
			minAmountToReviewTrips: null,
			maxTripAgeInDaysForAutoApproval: null,
		},
	};

	const form = useForm<Division>({
		defaultValues,
		mode: "onChange",
		shouldUnregister: false,
	});

	const { handleSubmit, formState, setValue, watch } = form;
	const { divisionService } = useServices();
	const [updateCmd, updateQueryState, , resetQueryState] = useAsyncCommand(
		divisionService.updateDivision
	);

	const handleSubmitUserForm = handleSubmit(async (values) => {
		const updated: Division = {
			...division,
			...values,
			pricingSettings: values.pricingSettings
				? checkPricingSettings(values.pricingSettings)
				: null,
			reviewSettings: values.reviewSettings?.shouldReviewTrips
				? values.reviewSettings
				: null,
		};
		const updatedValue = await updateCmd(
			division.divisionId,
			updated,
			defaultValues
		);
		if (updatedValue.state === "success") {
			form.reset({
				...updatedValue.data,
				pricingSettings: updatedValue.data.pricingSettings
					? initialPricingSettings(updatedValue.data.pricingSettings, products)
					: [],
			});
			context.updateDivision(updatedValue.data);
		}
	});

	const jobradSettings = watch("providerSettings.jobrad");

	useEffect(() => {
		if (
			defaultValues.providerSettings?.jobrad !== jobradSettings &&
			jobradSettings === undefined
		) {
			setValue("providerSettings.jobrad", undefined, { shouldDirty: true });
		}
	}, [jobradSettings]);

	// reset query state when form is dirty again after a successful update
	useEffect(() => {
		if (updateQueryState.state === "success" && formState.isDirty) {
			resetQueryState();
		}
	}, [formState.isDirty, updateQueryState.state, resetQueryState]);

	return (
		<PricingSettingsPageContext.Provider
			value={{ pricingSettings: division.pricingSettings }}
		>
			<FormContainer className="form">
				<FormProvider {...form}>
					<StyledForm onSubmit={handleSubmitUserForm}>
						<QueryError queryState={updateQueryState} />
						<BasicDataSettings />
						<TimerSettings />
						<ModuleCategorySettings />
						<ExcessBudgetSettings />
						<ReviewSettings
							initiallyEnabled={!!division.reviewSettings?.shouldReviewTrips}
						/>
						<OtherSettings />
						<PricingSettingsList />
						<ButtonContainer>
							<ActionButton
								type="submit"
								query={updateQueryState}
								disabled={!formState.isValid || !formState.isDirty}
								successContent="Gespeichert"
								disableAfterSuccess={false}
							>
								Speichern
							</ActionButton>
						</ButtonContainer>
					</StyledForm>
				</FormProvider>
			</FormContainer>
		</PricingSettingsPageContext.Provider>
	);
}

const StyledForm = styled(Form)`
	display: grid;
	row-gap: 3rem;
	padding-left: 2rem;
`;
